import { cn } from "@/lib/utils";
import { Brain, Sparkles, Trophy } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface KnowledgeScoreIndicatorProps {
  score: number;
  variant?: "compact" | "detailed" | "minimal" | "ring";
  className?: string;
  showLabel?: boolean;
  size?: "sm" | "md" | "lg";
  label?: string;
}

export function KnowledgeScoreIndicator({
  score,
  variant = "compact",
  className,
  showLabel = true,
  size = "md",
  label = "AI Knowledge",
}: KnowledgeScoreIndicatorProps) {
  const getScoreColor = (s: number) => {
    if (s < 30) return "text-red-500";
    if (s < 60) return "text-amber-500";
    if (s < 90) return "text-emerald-500";
    return "text-primary";
  };

  const getProgressColor = (s: number) => {
    if (s < 30) return "stroke-red-500";
    if (s < 60) return "stroke-amber-500";
    if (s < 90) return "stroke-emerald-500";
    return "stroke-primary";
  };

  const getBarColor = (s: number) => {
    if (s < 30) return "bg-red-500";
    if (s < 60) return "bg-amber-500";
    if (s < 90) return "bg-emerald-500";
    return "bg-primary";
  };

  const getBgColor = (s: number) => {
    if (s < 30) return "bg-red-500/10";
    if (s < 60) return "bg-amber-500/10";
    if (s < 90) return "bg-emerald-500/10";
    return "bg-primary/10";
  };

  const getGradient = (s: number) => {
    if (s < 30) return "from-red-400 to-red-600";
    if (s < 60) return "from-amber-400 to-amber-600";
    if (s < 90) return "from-emerald-400 to-emerald-600";
    return "from-primary to-primary";
  };

  if (variant === "ring") {
    const ringSize = size === "sm" ? 48 : size === "lg" ? 96 : 64;
    const strokeWidth = size === "sm" ? 4 : size === "lg" ? 8 : 6;
    const radius = (ringSize - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (score / 100) * circumference;

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn("relative inline-flex items-center justify-center", className)}>
              <svg
                width={ringSize}
                height={ringSize}
                className="transform -rotate-90"
              >
                <circle
                  cx={ringSize / 2}
                  cy={ringSize / 2}
                  r={radius}
                  className="stroke-muted fill-none"
                  strokeWidth={strokeWidth}
                />
                <circle
                  cx={ringSize / 2}
                  cy={ringSize / 2}
                  r={radius}
                  className={cn("fill-none transition-all duration-700 ease-out", getProgressColor(score))}
                  strokeWidth={strokeWidth}
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                {score >= 100 ? (
                  <Trophy className={cn(
                    "text-primary",
                    size === "sm" ? "h-4 w-4" : size === "lg" ? "h-8 w-8" : "h-5 w-5"
                  )} />
                ) : (
                  <span className={cn(
                    "font-bold tabular-nums",
                    getScoreColor(score),
                    size === "sm" ? "text-xs" : size === "lg" ? "text-xl" : "text-sm"
                  )}>
                    {score}%
                  </span>
                )}
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs font-medium">{label}: {score}%</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (variant === "minimal") {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn("flex items-center gap-1.5", className)}>
              <Brain className={cn("h-3.5 w-3.5", getScoreColor(score))} />
              <span className={cn("text-xs font-medium tabular-nums", getScoreColor(score))}>
                {score}%
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">{label}: {score}%</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (variant === "compact") {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                "flex items-center gap-2 px-2.5 py-1.5 rounded-full border",
                getBgColor(score),
                score >= 90 ? "border-primary dark:border-primary" : "border-transparent",
                className
              )}
            >
              {score >= 100 ? (
                <Sparkles className={cn("h-3.5 w-3.5", getScoreColor(score))} />
              ) : (
                <Brain className={cn("h-3.5 w-3.5", getScoreColor(score))} />
              )}
              <div className="flex items-center gap-1.5">
                {showLabel && (
                  <span className="text-xs text-muted-foreground">AI:</span>
                )}
                <div className="w-14 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={cn("h-full transition-all duration-500 rounded-full", getBarColor(score))}
                    style={{ width: `${score}%` }}
                  />
                </div>
                <span className={cn("text-xs font-semibold tabular-nums", getScoreColor(score))}>
                  {score}%
                </span>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs font-medium">{label}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Detailed variant
  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {score >= 100 ? (
            <div className={cn("p-1.5 rounded-lg", getBgColor(score))}>
              <Trophy className={cn("h-5 w-5", getScoreColor(score))} />
            </div>
          ) : (
            <div className={cn("p-1.5 rounded-lg", getBgColor(score))}>
              <Brain className={cn("h-5 w-5", getScoreColor(score))} />
            </div>
          )}
          <span className="text-sm font-medium">{label}</span>
        </div>
        <span className={cn("text-xl font-bold tabular-nums", getScoreColor(score))}>
          {score}%
        </span>
      </div>

      <div className="w-full h-2.5 bg-muted rounded-full overflow-hidden">
        <div
          className={cn(
            "h-full transition-all duration-700 ease-out rounded-full bg-gradient-to-r",
            getGradient(score)
          )}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
}
